import type { ProgressBreakpoints } from "../components/calorie-progress-bar/progress-bar-track";

const DAYS_PER_WEEK = 7;

export const DAILY_CALORIE_DEFICIT_TARGET = 500;
export const WEEKLY_CALORIE_DEFICIT_TARGET = DAILY_CALORIE_DEFICIT_TARGET * DAYS_PER_WEEK;

export const WEEKLY_CALORIE_DEFICIT_BREAKPOINTS: ProgressBreakpoints = [
  500,
  2_500,
  WEEKLY_CALORIE_DEFICIT_TARGET,
  4_500,
  5_500,
];

export type WeeklyCalorieDeficitBand =
  | "furthest-left"
  | "left"
  | "green"
  | "right"
  | "furthest-right";

export const WEEKLY_CALORIE_DEFICIT_MESSAGES: Record<WeeklyCalorieDeficitBand, string> = {
  "furthest-left": "Progress will be much slower but you can still reach the green 👍",
  left: "Still in a deficit. Still making progress ✅",
  green: "On track so far ✅",
  right: "Careful, your deficit is getting too large 👀",
  "furthest-right": "Your deficit is too large. Let’s get you back in the green ⚠️",
};

/**
 * The backend deviation is measured from the user's calorie target, which already
 * includes the daily 500 kcal deficit. Subtract only that deviation from the seven
 * daily deficit chunks; taking another 500 kcal off the calorie target would count
 * the deficit twice.
 */
export const calculateWeeklyCalorieDeficit = (totalCalorieDeviation: number) =>
  Math.round(WEEKLY_CALORIE_DEFICIT_TARGET - totalCalorieDeviation);

export const getWeeklyCalorieDeficitBand = (
  weeklyCalorieDeficit: number,
): WeeklyCalorieDeficitBand => {
  const [furthestLeftMax, greenMin, , greenMax, furthestRightMin] =
    WEEKLY_CALORIE_DEFICIT_BREAKPOINTS;

  if (weeklyCalorieDeficit <= furthestLeftMax) {
    return "furthest-left";
  }

  if (weeklyCalorieDeficit < greenMin) {
    return "left";
  }

  if (weeklyCalorieDeficit <= greenMax) {
    return "green";
  }

  if (weeklyCalorieDeficit <= furthestRightMin) {
    return "right";
  }

  return "furthest-right";
};

export const getWeeklyCalorieDeficitMessage = (weeklyCalorieDeficit: number) =>
  WEEKLY_CALORIE_DEFICIT_MESSAGES[getWeeklyCalorieDeficitBand(weeklyCalorieDeficit)];
